/**
 * The plan checks for components, and the store of the one upgrade dialog
 * (rendered by <PlanGateHost /> in App.tsx).
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEntitlements, useMyEntitlements } from "@/hooks/useEntitlements";
import {
  PERSONAL_FEATURES,
  featureError,
  limitError,
  personalFeatureError,
  type LimitId,
  type PlanError,
} from "@/lib/billing/entitlements";
import { readPlanError } from "@/lib/billing/planError";
import type { FeatureId } from "@/lib/pricing/plans";

// The one upgrade dialog of the app: which plan error it shows, if any. A tiny
// store instead of a context, so the host sits next to <Routes> in App.tsx
// without wrapping (and re-indenting) every route.
let shownPlanError: PlanError | null = null;
export const getShownPlanError = () => shownPlanError;
const listeners = new Set<() => void>();
export function setShownPlanError(err: PlanError | null) {
  shownPlanError = err;
  listeners.forEach((l) => l());
}
export function subscribePlanError(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The plan checks for components. The server decides (billing_check() and its
 * guards); these only explain, and ask before a click the server would refuse.
 * Nothing opens until billing is visible and billing_config.enforce is on,
 * except a real refusal from the server.
 */
export function usePlanGate() {
  const { entitlements, active } = useEntitlements();
  const { mine, active: personalActive } = useMyEntitlements();
  const queryClient = useQueryClient();

  const showPlanError = useCallback(
    (err: PlanError) => {
      setShownPlanError(err);
      // The usage behind the refusal changed; show fresh numbers next time.
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    [queryClient]
  );

  /** For a failed call: when the plan refused it, opens the dialog and resolves true. */
  const handlePlanError = useCallback(
    async (error: unknown) => {
      const planError = await readPlanError(error);
      if (planError) showPlanError(planError);
      return !!planError;
    },
    [showPlanError]
  );

  /** Before adding: true (and the dialog) when the plan's limit is reached. */
  const blockedByLimit = useCallback(
    (limit: LimitId, adding = 1) => {
      if (!active || !entitlements) return false;
      const err = limitError(entitlements, limit, adding);
      if (err) setShownPlanError(err);
      return !!err;
    },
    [active, entitlements]
  );

  /**
   * The plan error a Firma-only action would get, or null. Personal features
   * (private mode) follow the person's best plan over the companies they own,
   * not the selected company.
   */
  const featureBlock = useCallback(
    (feature: FeatureId): PlanError | null => {
      if (PERSONAL_FEATURES.includes(feature)) {
        return personalActive && mine ? personalFeatureError(mine, feature) : null;
      }
      return active && entitlements ? featureError(entitlements, feature) : null;
    },
    [active, entitlements, personalActive, mine]
  );

  /** Before a Firma-only action: true (and the dialog) when the plan lacks it. */
  const blockedByFeature = useCallback(
    (feature: FeatureId) => {
      const err = featureBlock(feature);
      if (err) setShownPlanError(err);
      return !!err;
    },
    [featureBlock]
  );

  return {
    entitlements,
    /** Billing visible and enforced by the server: the UI gates. */
    active,
    showPlanError,
    handlePlanError,
    blockedByLimit,
    blockedByFeature,
    /** The refusal a feature would get, without opening the dialog (route guards). */
    featureBlock,
  };
}
